import React, { useState, useEffect } from "react";
import { HoverDeleteButton } from "@scripts/react/components/Button";
import { twClassNames } from "@scripts/react/helpers/twClassNames";

interface FloatingHintProps {
    isVisible: boolean;
    message: string | React.ReactNode;
    isDismissable?: boolean;
    className?: string;
    lightMode?: boolean;
}

export const FloatingHint: React.FC<FloatingHintProps> = ({
    isVisible,
    message,
    isDismissable = true,
    className,
    lightMode = false
}) => {
    const [isDismissed, setIsDismissed] = useState(false);
    const [shouldRender, setShouldRender] = useState(false);

    // Reset dismissed state when message changes or visibility changes
    useEffect(() => {
        setIsDismissed(false);
    }, [message, isVisible]);

    // Handle mounting/unmounting with delay for exit animation
    useEffect(() => {
        if (isVisible && !isDismissed) {
            setShouldRender(true);
        } else {
            const timeout = setTimeout(() => {
                setShouldRender(false);
            }, 500); // Match transition duration
            return () => clearTimeout(timeout);
        }
    }, [isVisible, isDismissed]);

    if (!shouldRender) return null;

    return (
        <div
            className={twClassNames(
                "group relative z-10 flex select-none items-center gap-2 rounded-lg border px-4 py-3 text-center text-sm font-medium shadow-lg backdrop-blur-lg transition-all duration-500 ease-out",
                lightMode
                    ? "border-divider bg-white text-primary"
                    : "border-secondary bg-primary/80 text-white",
                isVisible && !isDismissed
                    ? "translate-y-0 opacity-100"
                    : "-translate-y-5 opacity-0",
                className
            )}
            style={{
                boxShadow: `inset -1px -1px 4px 0 rgba(0, 0, 0, ${lightMode ? 0.1 : 0.5}), inset 1px 1px 4px 0 rgba(255, 255, 255, ${lightMode ? 0.1 : 0.5})`
            }}
        >
            {typeof message === "string" ? <div className="text-center">{message}</div> : message}
            {isDismissable && (
                <HoverDeleteButton
                    onClick={() => setIsDismissed(true)}
                    ariaLabel="Dismiss hint"
                    className="absolute -right-1.5 -top-1.5"
                />
            )}
        </div>
    );
};
